import { Controller } from '@hotwired/stimulus';
import { toggleCSSClasses } from '../js/utils';

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static targets = [
        'navLink',
        'menuDestinations',
        'menuSports',
        'menuConcept',
    ];
    static classes = ['active', 'hidden'];

    connect() {
        document.body.addEventListener('click', this.handleOutsideClick);
    }

    disconnect() {
        document.body.removeEventListener('click', this.handleOutsideClick);
    }

    hasTargetForMenu(menuName) {
        switch (menuName) {
            case 'menuDestinations':
                return this.hasMenuDestinationsTarget;
            case 'menuSports':
                return this.hasMenuSportsTarget;
            case 'menuConcept':
                return this.hasMenuConceptTarget;
            default:
                return false;
        }
    }

    handleOutsideClick = (event) => {
        const isClickInside =
            this.element.contains(event.target) ||
            (this.hasMenuDestinationsTarget &&
                this.menuDestinationsTarget.contains(event.target)) ||
            (this.hasMenuSportsTarget &&
                this.menuSportsTarget.contains(event.target)) ||
            (this.hasMenuConceptTarget &&
                this.menuConceptTarget.contains(event.target));

        if (!isClickInside) {
            this.closeAllMenus();
        }
    };

    openMenuLevel1(event) {
        event.preventDefault();
        const menuName = event.params.ref;

        if (!this.hasTargetForMenu(menuName)) return;

        const menuTarget = this[`${menuName}Target`];

        // Close all other menus
        this.closeAllMenus(menuName);

        // Toggle current menu
        const isOpening = menuTarget.classList.contains('hidden');
        toggleCSSClasses(menuTarget, 'hidden');

        if (isOpening) {
            toggleCSSClasses(event.currentTarget, 'font-semibold', 'underline');
        } else {
            event.currentTarget.classList.remove('font-semibold', 'underline');
        }

        // Auto-open "Notre sélection" if menu just opened
        if (isOpening) {
            this.openDefaultSection(menuTarget, menuName);
        }
    }

    openMenuLevel(event) {
        const { slug, level } = event.params;
        const button = event.currentTarget;
        const section = document.getElementById(`level${level}-${slug}`);

        if (!section) return;

        const isOpen = button.hasAttribute('data-active');

        // If clicking the same button, just toggle it
        if (isOpen) {
            this.closeSection(section, button);
            // Also close child levels
            if (level === 2) {
                this.closeSectionsAtLevel(3);
            }
            return;
        }

        // Close all other sections at this level (but not the current one)
        this.closeSectionsAtLevel(level, slug);

        // Open this section
        this.openSection(section, button);

        // Close child sections when opening a new parent
        if (level === 2) {
            this.closeSectionsAtLevel(3);
        }
    }

    openDefaultSection(menuContainer, menuName) {
        const sectionMap = {
            menuDestinations: 'destination-selection',
            menuSports: 'sport-selection',
        };

        const selectionSlug = sectionMap[menuName];
        if (!selectionSlug) return;

        const section = menuContainer.querySelector(`#level2-${selectionSlug}`);
        const button = menuContainer.querySelector(
            `[data-menu-desktop-slug-param="${selectionSlug}"]`,
        );

        if (section && button) {
            this.openSection(section, button);
        }
    }

    openSection(section, button) {
        section.classList.remove('hidden');
        toggleCSSClasses(button, 'font-semibold', 'text-content-brand');
        button.setAttribute('data-active', '');
    }

    closeSection(section, button) {
        toggleCSSClasses(section, 'hidden');
        button.classList.remove('font-semibold', 'text-content-brand');
        button.removeAttribute('data-active');
    }

    closeSectionsAtLevel(level, exceptSlug = null) {
        document
            .querySelectorAll(
                `[data-menu-desktop-level-param="${level}"][data-active]`,
            )
            .forEach((button) => {
                const slug = button.dataset.menuDesktopSlugParam;
                // Don't close the section we're about to open
                if (slug !== exceptSlug) {
                    const section = document.getElementById(
                        `level${level}-${slug}`,
                    );
                    if (section) {
                        this.closeSection(section, button);
                    }
                }
            });
    }

    closeAllMenus(exceptMenu = null) {
        // Close all menu containers
        ['menuDestinations', 'menuSports', 'menuConcept'].forEach(
            (menuName) => {
                if (
                    menuName !== exceptMenu &&
                    this.hasTargetForMenu(menuName)
                ) {
                    const target = this[`${menuName}Target`];
                    if (target && !target.classList.contains('hidden')) {
                        target.classList.add('hidden');
                    }
                }
            },
        );

        // Reset nav links
        this.navLinkTargets.forEach((link) => {
            link.classList.remove('font-semibold', 'underline');
        });

        // Close all submenus
        document.querySelectorAll('[data-active]').forEach((button) => {
            button.classList.remove('font-semibold', 'text-content-brand');
            button.removeAttribute('data-active');
        });

        document.querySelectorAll('[id^="level"]').forEach((section) => {
            if (!section.classList.contains('hidden')) {
                toggleCSSClasses(section, 'hidden');
            }
        });
    }
}
